import { useEffect, useRef } from "react";

/**
 * Syncs a request object with the URL search parameters and updates window history so that backwards/
 * forwards navigation preserves previously selected search parameters.
 *
 * Note:
 * - Array parameters currently only store the first value in the URL (see below for explanation); single-valued
 *   parameters are stored normally.
 * 
 * Example:
 * Request: {"page":"1", "facet":["investigator:David A. Wise"], "perPage":"50", "startDate":"2025-09-09"}
 * URL: page=1&perPage=50&facet=investigator%3ADavid+A.+Wise&startDate=2025-09-09
 *
 * @param {Object} request - current request object
 */
export const useSearchParamsSync = (request) => {
    const isNavigate = useRef(false); // Indicates if re-render was caused by user navigating forwards/backwards
    const isFirstLoad = useRef(true); // Indicates if this is the first page load

    useEffect(() => {
        // Don't update window history if user is navigating forwards/backwards
        if (isNavigate.current) { 
            isNavigate.current = false;
            return;
        }

        const url = new URL(window.location.href);
        const searchParams = url.searchParams;
        Object.entries(request).forEach(([key, value]) => {
            if ((Array.isArray(value) && value.length !== 0)) {
                // DataProvider uses query-string to parse URL params before making a request
                // By default, query-string uses duplicate keys to parse arrays with elements
                // value.forEach((item) => {
                //     if (!searchParams.has(key, value)) {
                //         searchParams.append(key, item);
                //     }
                // });

                // Since multi-valued search facets are currently not supported, for now, just take the first value in the array
                searchParams.set(key, value[0]);
            } else if (!Array.isArray(value) && value !== null) { // Need to check if it's not an array, since could be empty array
                searchParams.set(key, value);
            } else {
                searchParams.delete(key); 
            }
        });

        // On first load, the browser will push a history entry (URL without params), so replace it with the updated URL with params.
        if (isFirstLoad.current) {
            window.history.replaceState(null, "", url);
            isFirstLoad.current = false;
        } else {
            // If this is not the first load but the search params have changed, the browser will not push a new entry. 
            // We need to manually push a new entry to update the URL's search params.
            window.history.pushState(null, "", url);
        }
    }, [JSON.stringify(request)]);

    useEffect(() => {
        const handlePopstate = () => {
            isNavigate.current = true;
        }
        window.addEventListener("popstate", handlePopstate);

        return () => window.removeEventListener("popstate", handlePopstate);
    }, [])
}